import { Box, Typography } from "@mui/material";
import { useGetOne } from "react-admin";
import {
  AutovioCalendarEvent,
  isPaidLesson,
  isTheoryExam,
  isTheoryLesson,
  PaidLesson,
  TheoryLesson,
} from "../../model/autovioCalendarEvents";
import { formatDateTime } from "../../utils/calendar";
import { type Student } from "../../providers/studentsProvider";

export function ShortEventHistory(props: { event: AutovioCalendarEvent; studentContext?: Student }) {
  const { event, studentContext } = props;
  return isPaidLesson(event) ? (
    <_ShortPaidLessonHistory event={event} />
  ) : isTheoryLesson(event) ? (
    <_ShortTheoryLessonHistory event={event} studentContext={studentContext} />
  ) : (
    <_ShortEventHistory event={event} />
  );
}

function _ShortPaidLessonHistory(props: { event: PaidLesson }) {
  const { event } = props;
  const { data: student } = useGetOne("students", { id: event.student.uid });
  const { createdAt, createdBy: createdById } = event;
  const imported = createdById === "00000000-0000-0000-0000-000000000000";
  const { data: createdBy } = useGetOne("users", { id: createdById }, { enabled: !imported && !!createdById });
  const createdByStudent = event.createdBy === event.student.uid;
  const bookedAt = event.student.bookedAt;
  const declinedAt = event.student.declinedAt;
  const canceledByStudentAt = event.student.canceledAt;
  const cancelReason = event.student.cancelReason;
  const { deletedAt, deletedBy: deletedById, deletionReason } = event;
  const { data: deletedBy } = useGetOne("users", { id: deletedById }, { enabled: !!deletedById });
  return (
    <Box sx={{ color: "text.disabled", mb: 2, "& > .MuiTypography-root": { fontSize: "14px" } }}>
      {imported && <Typography>Importiert am {formatDateTime(createdAt)}</Typography>}
      {createdByStudent && (
        <Typography>
          Erstellt/Gebucht am {formatDateTime(createdAt)} von {createdBy?.name ?? "..."}
        </Typography>
      )}
      {!(imported || createdByStudent) && (
        <Typography>
          Erstellt am {formatDateTime(createdAt)} von {createdBy?.name ?? "..."}
        </Typography>
      )}
      {!(imported || createdByStudent) && bookedAt && (
        <Typography>
          {bookedAt > event.start ? "Bestätigt" : "Gebucht"} am {formatDateTime(bookedAt)} von {student?.name ?? "..."}
        </Typography>
      )}
      {declinedAt && (
        <Typography>
          Abgelehnt am {formatDateTime(declinedAt)} von {student?.name ?? "..."}
        </Typography>
      )}
      {canceledByStudentAt && (
        <Typography>
          Abgesagt am {formatDateTime(canceledByStudentAt)} von {student?.name ?? "..."}
        </Typography>
      )}
      {canceledByStudentAt && cancelReason && <Typography>Grund: {cancelReason}</Typography>}
      {event.deleted && deletedAt && (
        <Typography>
          Abgesagt am {formatDateTime(deletedAt)} von {deletedBy?.name ?? "..."}
        </Typography>
      )}
      {event.deleted && deletedAt && deletionReason && <Typography>Grund: {deletionReason}</Typography>}
    </Box>
  );
}

function _ShortTheoryLessonHistory(props: { event: TheoryLesson; studentContext?: Student }) {
  const { event, studentContext } = props;
  const { createdAt, createdBy: createdById } = event;
  const imported = createdById === "00000000-0000-0000-0000-000000000000";
  const { data: createdBy } = useGetOne("users", { id: createdById }, { enabled: !imported && !!createdById });
  const attendance = studentContext && event.students?.[studentContext.id];
  const bookedAt = attendance?.bookedAt;
  let bookedById = attendance?.bookedBy;
  // If `bookedBy` is not stored in `attendance`, we fall back to the student id
  // if and only if `agreedCancellationPolicy` is set, which indicates that the theory lesson
  // was indeed booked by the student ...
  if (!bookedById && attendance?.agreedCancellationPolicy) {
    bookedById = studentContext?.id;
  }
  const { data: bookedBy } = useGetOne("users", { id: bookedById }, { enabled: !!bookedById });
  const canceledAt = attendance?.canceledAt;
  const canceledById = attendance?.canceledBy;
  const { data: canceledBy } = useGetOne("users", { id: canceledById }, { enabled: !!canceledById });
  const cancelReason = attendance?.cancelReason;
  const { deletedAt, deletedBy: deletedById, deletionReason } = event;
  const { data: deletedBy } = useGetOne("users", { id: deletedById }, { enabled: !!deletedById });
  return (
    <Box sx={{ color: "text.disabled", mb: 2 }}>
      {imported && <Typography>Importiert am {formatDateTime(createdAt)}</Typography>}
      {!imported && (
        <Typography>
          Erstellt am {formatDateTime(createdAt)} von {createdBy?.name ?? "..."}
        </Typography>
      )}
      {bookedAt && (
        <Typography>
          Gebucht am {formatDateTime(bookedAt)}
          {bookedById && ` von ${bookedBy?.name ?? "..."}`}
        </Typography>
      )}
      {canceledAt && (
        <Typography>
          Abgesagt am {formatDateTime(canceledAt)}
          {canceledById && ` von ${canceledBy?.name ?? "..."}`}
        </Typography>
      )}
      {canceledAt && cancelReason && <Typography>Grund: {cancelReason}</Typography>}
      {event.deleted && deletedAt && (
        <Typography>
          Abgesagt am {formatDateTime(deletedAt)} von {deletedBy?.name ?? "..."}
        </Typography>
      )}
      {event.deleted && deletedAt && deletionReason && <Typography>Grund: {deletionReason}</Typography>}
    </Box>
  );
}

function _ShortEventHistory(props: { event: AutovioCalendarEvent }) {
  const { event } = props;
  const { createdAt, createdBy: createdById } = event;
  const imported = createdById === "00000000-0000-0000-0000-000000000000";
  const { data: createdBy } = useGetOne("users", { id: createdById }, { enabled: !imported && !!createdById });
  const theoryExamStudentId = isTheoryExam(event) ? event.student.uid : undefined;
  const theoryExamDeclinedAt = isTheoryExam(event) ? event.student.declinedAt : undefined;
  const { data: declinedByStudent } = useGetOne<Student>(
    "students",
    { id: theoryExamStudentId },
    { enabled: !!theoryExamDeclinedAt && !!theoryExamStudentId },
  );
  const { deletedAt, deletedBy: deletedById, deletionReason } = event;
  const { data: deletedBy } = useGetOne("users", { id: deletedById }, { enabled: !!deletedById });
  return (
    <Box sx={{ color: "text.disabled", mb: 2, "& > .MuiTypography-root": { fontSize: "14px" } }}>
      {imported && <Typography>Importiert am {formatDateTime(createdAt)}</Typography>}
      {!imported && (
        <Typography>
          Erstellt am {formatDateTime(createdAt)} von {createdBy?.name ?? "..."}
        </Typography>
      )}
      {theoryExamDeclinedAt && (
        <Typography>
          Abgelehnt am {formatDateTime(theoryExamDeclinedAt)} von {declinedByStudent?.name ?? "..."}
        </Typography>
      )}
      {event.deleted && deletedAt && (
        <Typography>
          Gelöscht am {formatDateTime(deletedAt)} von {deletedBy?.name ?? "..."}
        </Typography>
      )}
      {event.deleted && deletedAt && deletionReason && <Typography>Grund: {deletionReason}</Typography>}
    </Box>
  );
}
